import React, { useCallback, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import Action from './Action';
import { Item, ItemId } from '../../types/items';
import ModalConfirm from '../modal-confirm/ModalConfirm';
import { TrashAltIcon } from '../icon/regular';
import hotKeyConverter from '../../utility/keyCodeConverter';
import { selectHoveredItemId } from '../../store/modules/lists/selectors';

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  icon: {
    color: palette.icons.delete,
    fontSize: '1rem',
  },
}));

type EditActionProps = {
  original: Item;
  onDelete: (id: ItemId) => void;
};

const DeleteAction = ({ original, onDelete }: EditActionProps) => {
  const classes = useStyles();
  const [confirmOpened, setConfirmOpened] = useState(false);
  const hoveredItemId = useSelector(selectHoveredItemId);

  const clickHandler = useCallback(() => {
    if (!confirmOpened) {
      setConfirmOpened(true);
    }
  }, [setConfirmOpened, confirmOpened]);

  const closeHandler = useCallback(() => {
    setConfirmOpened(false);
  }, [setConfirmOpened]);

  const confirmHandler = useCallback(() => {
    closeHandler();
    onDelete(original.id);
  }, [closeHandler, onDelete, original.id]);

  useEffect(() => {
    const hotKeyHandler = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.keyCode === hotKeyConverter('delete')) {
        if (hoveredItemId === original.id) {
          event.preventDefault();
          clickHandler();
        }
      }
    };
    window.addEventListener('keydown', hotKeyHandler);
    return () => {
      window.removeEventListener('keydown', hotKeyHandler);
    };
  }, [clickHandler, hoveredItemId, original]);

  return (
    <Action
      icon={<TrashAltIcon className={classes.icon} />}
      title="Delete"
      disabled={!original.permissions.can_delete}
      onClick={clickHandler}
    >
      <ModalConfirm
        open={confirmOpened}
        onClose={closeHandler}
        onCancel={closeHandler}
        onConfirm={confirmHandler}
      />
    </Action>
  );
};

export default DeleteAction;
